import { useState, useEffect, useCallback } from 'react';
import { AtendimentoService } from '../services/atendimentoService';
import { Atendimento } from '../types';

export const useAtendimentos = (organizationId: string) => {
  const [atendimentos, setAtendimentos] = useState<Atendimento[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadQueue = useCallback(async () => {
    setLoading(true);
    setError(null);

    const response = await AtendimentoService.getQueue(organizationId);

    if (response.success && response.data) {
      setAtendimentos(response.data);
    } else {
      setError(response.message);
    }

    setLoading(false);
  }, [organizationId]);

  const addToQueue = useCallback(async (pacienteId: string, isPriority: boolean = false) => {
    const response = await AtendimentoService.addToQueue(organizationId, {
      paciente_id: pacienteId,
      is_priority: isPriority
    });

    if (response.success) {
      await loadQueue();
    }

    return response;
  }, [organizationId, loadQueue]);

  const callPatient = useCallback(async (atendimentoId: string) => {
    const response = await AtendimentoService.callPatient(atendimentoId);

    if (response.success) {
      await loadQueue();
    }

    return response;
  }, [loadQueue]);

  const finishAttendance = useCallback(async (atendimentoId: string) => {
    const response = await AtendimentoService.finishAttendance(atendimentoId);

    if (response.success) {
      await loadQueue();
    }

    return response;
  }, [loadQueue]);

  const cancelAttendance = useCallback(async (atendimentoId: string) => {
    const response = await AtendimentoService.cancelAttendance(atendimentoId);

    if (response.success) {
      await loadQueue();
    }

    return response;
  }, [loadQueue]);

  useEffect(() => {
    if (organizationId) {
      loadQueue();

      const subscription = AtendimentoService.subscribeToQueue(organizationId, () => {
        loadQueue();
      });

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [organizationId, loadQueue]);

  return {
    atendimentos,
    loading,
    error,
    addToQueue,
    callPatient,
    finishAttendance,
    cancelAttendance,
    refetch: loadQueue
  };
};
